import type { ReactElement } from "react"
import React from "react"
import { useTranslation } from "react-i18next"

import { NzDataGrid, defaultRowHeight } from "@/components/nzDataGrid"
import type { BillPdfPreviewRow } from "@/modules/data/services/use-bill-pdf-upload-session"
import { isUnsavedPreviewRow } from "@/modules/data/utils/bill-pdf-preview-state"
import {
  GridActionTooltipButton,
  baseColumnDef,
  baseNumberColumnDef,
} from "@/nzds/data-grid"
import { useCommonBillColumns } from "@/utils/bill/use-common-bill-columns"
import { usePeriodColumn } from "@/utils/grid-columns/use-period-column"
import { useResourceColumn } from "@/utils/grid-columns/use-resource-column"
import { useUtilityProviderColumn } from "@/utils/grid-columns/use-utility-provider-column"
import { getDisplayValue } from "@/utils/value"

import DeleteIcon from "@mui/icons-material/Delete"
import EditIcon from "@mui/icons-material/Edit"
import VisibilityIcon from "@mui/icons-material/Visibility"
import type { ChipProps } from "@mui/material"
import { Box, Chip } from "@mui/material"
import type { GridActionsCellItemProps, GridColDef } from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"

import {
  billPdfPreviewActionsColumnWidth,
  billPdfPreviewNestedGridInsetPx,
} from "./bill-pdf-preview-actions-column-width"
import { BillPdfRecommendationChip } from "./bill-pdf-recommendation-chip"

interface BillPdfPreviewBillsPanelProps {
  label: string
  onEdit?: (row: BillPdfPreviewRow) => void
  onRemove?: (rowId: string) => void
  onView: (row: BillPdfPreviewRow) => void
  rows: BillPdfPreviewRow[]
}

interface BillRowActionLabels {
  edit: string
  remove: string
  view: string
}

interface BillRowActionsOptions {
  labels: BillRowActionLabels
  onEdit?: (row: BillPdfPreviewRow) => void
  onRemove?: (rowId: string) => void
  onView: (row: BillPdfPreviewRow) => void
  row: BillPdfPreviewRow
}

// Rows are camelCased by the session service, unlike snake_cased bill records
const PREVIEW_GRID_FIELDS = {
  flowDirection: "flowDirection",
  meterCode: "meterCode",
  siteName: "siteName",
} as const

const ROW_STATUS_CHIP_COLORS: Record<
  BillPdfPreviewRow["status"],
  ChipProps["color"]
> = {
  error: "error",
  imported: "default",
  ready: "success",
}

const buildPlainColumn = (
  base: Partial<GridColDef<BillPdfPreviewRow>>,
  field: string,
  headerName: string
): GridColDef<BillPdfPreviewRow> => ({
  ...base,
  field,
  filterable: false,
  flex: 1,
  headerName,
  minWidth: 100,
  sortable: false,
})

const getBillRowActions = ({
  labels,
  onEdit,
  onRemove,
  onView,
  row,
}: BillRowActionsOptions): ReactElement<GridActionsCellItemProps>[] => {
  if (!isUnsavedPreviewRow(row)) {
    return []
  }

  const actions: ReactElement<GridActionsCellItemProps>[] = [
    <GridActionsCellItem
      component={GridActionTooltipButton}
      icon={<VisibilityIcon color="primary" />}
      key="view-action"
      label={labels.view}
      onClick={() => {
        onView(row)
      }}
      showInMenu={false}
    />,
  ]

  if (onEdit) {
    actions.push(
      <GridActionsCellItem
        component={GridActionTooltipButton}
        icon={<EditIcon color="primary" />}
        key="edit-action"
        label={labels.edit}
        onClick={() => {
          onEdit(row)
        }}
        showInMenu={false}
      />
    )
  }

  if (onRemove) {
    actions.push(
      <GridActionsCellItem
        component={GridActionTooltipButton}
        icon={<DeleteIcon />}
        key="remove-action"
        label={labels.remove}
        onClick={() => {
          onRemove(row.id)
        }}
        showInMenu={false}
      />
    )
  }

  return actions
}

export const BillPdfPreviewBillsPanel: React.FC<
  BillPdfPreviewBillsPanelProps
> = ({ label, onEdit, onRemove, onView, rows }) => {
  const { t } = useTranslation("data")
  const { t: tCommon } = useTranslation("common")
  const commonBillColumns = useCommonBillColumns<BillPdfPreviewRow>({
    fields: PREVIEW_GRID_FIELDS,
  })
  const periodColumn = usePeriodColumn<BillPdfPreviewRow>({
    endField: "endDate",
    startField: "startDate",
  })
  const resourceColumn = useResourceColumn<BillPdfPreviewRow>({
    field: "resourceType",
  })
  const utilityProviderColumn = useUtilityProviderColumn<BillPdfPreviewRow>({
    field: "utilityProvider",
  })
  const usageColumn = buildPlainColumn(
    baseNumberColumnDef,
    "usage",
    tCommon("fields.usage.title")
  )
  const usageUnitColumn = buildPlainColumn(
    baseColumnDef,
    "usageUnit",
    tCommon("editBill.usageUnitLabel")
  )
  const costColumn = buildPlainColumn(
    baseNumberColumnDef,
    "cost",
    tCommon("fields.cost.title")
  )
  const currencyColumn = buildPlainColumn(
    baseColumnDef,
    "currency",
    tCommon("fields.currency.title")
  )
  const actionsColumnWidth = billPdfPreviewActionsColumnWidth(
    onEdit != null || onRemove != null
  )
  const rowActionLabels: BillRowActionLabels = {
    edit: tCommon("button.edit"),
    remove: tCommon("button.remove"),
    view: tCommon("button.view"),
  }
  const rowStatusLabels: Record<BillPdfPreviewRow["status"], string> = {
    error: t("upload.billPdf.rowStatusError"),
    imported: t("upload.billPdf.rowStatusImported"),
    ready: t("upload.billPdf.rowStatusReady"),
  }

  const columns: GridColDef<BillPdfPreviewRow>[] = [
    {
      ...baseColumnDef,
      field: "status",
      filterable: false,
      flex: 0.7,
      headerName: t("upload.billPdf.columnStatus"),
      maxWidth: 140,
      minWidth: 100,
      sortable: false,
      renderCell: ({ row }) => (
        <Chip
          color={ROW_STATUS_CHIP_COLORS[row.status]}
          label={rowStatusLabels[row.status]}
          size="small"
          variant="outlined"
        />
      ),
      valueGetter: (_value, row) => rowStatusLabels[row.status],
      width: 140,
    },
    {
      ...baseColumnDef,
      field: "recommendation",
      filterable: false,
      headerName: t("upload.billPdf.columnRecommendation"),
      minWidth: 120,
      sortable: false,
      renderCell: ({ row }) =>
        row.recommendation ? (
          <BillPdfRecommendationChip
            band={row.recommendation.band}
            recommendation={row.recommendation}
          />
        ) : (
          getDisplayValue(null)
        ),
      valueGetter: (_value, row) => row.recommendation?.band,
      width: 160,
    },
    commonBillColumns.siteName,
    commonBillColumns.meterCode,
    utilityProviderColumn,
    resourceColumn,
    periodColumn,
    usageColumn,
    usageUnitColumn,
    costColumn,
    currencyColumn,
    commonBillColumns.flowDirection,
    {
      ...baseColumnDef,
      align: "left",
      cellClassName: "actions",
      field: "actions",
      filterable: false,
      getActions: ({ row }) =>
        getBillRowActions({
          labels: rowActionLabels,
          onEdit,
          onRemove,
          onView,
          row,
        }),
      headerAlign: "left",
      headerName: tCommon("gridColumn.actions.name"),
      minWidth: actionsColumnWidth,
      sortable: false,
      type: "actions",
      width: actionsColumnWidth,
    },
  ]

  return (
    <Box
      data-testid="bill-pdf-preview-bills"
      sx={(theme) => ({
        backgroundColor: theme.palette.grey[50],
        padding: `${billPdfPreviewNestedGridInsetPx}px`,
        width: "100%",
      })}
    >
      <NzDataGrid
        aria-label={label}
        autoHeight
        columnHeaderHeight={defaultRowHeight}
        columns={columns}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        disableRowSelectionOnClick
        disableVirtualization
        getRowId={(row: BillPdfPreviewRow) => row.id}
        hideFooter
        pinnedColumns={{ right: ["actions"] }}
        rows={rows}
        sx={{ borderRadius: 1 }}
      />
    </Box>
  )
}
